import React, { useEffect, useMemo, forwardRef, useCallback } from 'react';
import { Alert, Button, Form } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes , generateFormFields } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import _ from 'lodash';
import { useRef } from 'react';
import { useState } from 'react';

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  projectList: [{ application: undefined }]
};

export const DELIMITER = "/";
export const ENV_DELIMITER = ";"


const StackhawkMappingForm = forwardRef(({disableProjectFields = false, ...props}, ref) => {
  const applicationRef = useRef({});
  const environmentRef = useRef({});
  const environmentsApiRef = useRef([]);
  const [form] = Form.useForm();
  const cancelRef = useRef([]);
  const toolConfigId = props.record ? props.record.id : undefined;
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [{ ...initialFormValues }];
  });
  const [environmentChanged, setEnvironmentChanged]  = useState(false);
 
  const [mappedApplicationEnvironmentList] = useState(() => {

    if(props.record){
      const { appId, appName } = props.record;
      const applicationEnvironmentNameArray = appName.split(",");
      const applicationEnvironmentIdArray = appId.split(",");
      const applications = [], environments = {}

      _.forEach(applicationEnvironmentIdArray, (applicationEnvironmentId, i) => {
        const [applicationId, envId] = applicationEnvironmentId.split(DELIMITER);
        const index = applicationEnvironmentNameArray[i].lastIndexOf(DELIMITER)

        const applicationName = applicationEnvironmentNameArray[i].substring(0, index === -1 ? undefined : index);
        const environmentName = index === -1 ? undefined : applicationEnvironmentNameArray[i].substring(index + 1)
        applications.push({ id: applicationId, name: applicationName , applications, });
        applicationRef.current = {...applicationRef.current, [applicationId]: applicationName }
        if(envId){
          const environmentNameArray = environmentName.split(ENV_DELIMITER);

          _.forEach(envId.split(ENV_DELIMITER), (verId, index) => {

            environments[applicationId] = { id: verId, name: environmentNameArray[index] }
            environmentRef.current = {...environmentRef.current, [verId]: environmentNameArray[index] }
            
          })
        }

      });
      return {
        applications: _.uniqBy(applications, "id"),
        environments: environments
      }
    }
    return { applications: [], environments: {} }
  })

  useEffect(() => {
    // cancelling all version APIs on component unmount
    const cancels = cancelRef.current;
    return () => _.forEach(cancels, cancel => cancel());
  }, []);

  const fetchEnvironment = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, toolConfigId,  ...params });
    return {
      promise: promise.then(response => {
        // merging the already mapped versions to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedApplicationEnvironmentList.environments[params.project] && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, mappedApplicationEnvironmentList.environments[params.project]];
        }
        _.forEach(response.projects, p => {
          environmentRef.current[p.id] = p.name;
        });
        // merging the already mapped versions to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedApplicationEnvironmentList.environments[params.application] && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, mappedApplicationEnvironmentList.environments[params.project]];
        }
        return response;
      }),
      cancel
    }
  }, [props.loginConfigId, props.toolName, mappedApplicationEnvironmentList, toolConfigId]);

  const fetchApplications = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId,toolConfigId,  ...params });
    return {
      promise: promise.then(response => {
        // merging the already mapped versions to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedApplicationEnvironmentList.applications.length > 0 && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, ...mappedApplicationEnvironmentList.applications];
        }
        _.forEach(response.projects, p => {
          applicationRef.current[p.id] = p.name;
        });
        // merging the already mapped versions to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedApplicationEnvironmentList.applications.length > 0 && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, ...mappedApplicationEnvironmentList.applications];
        }
        return response;
      }),
      cancel
    }
  }, [props.loginConfigId,props.toolName, mappedApplicationEnvironmentList, toolConfigId]);

  const getExtraFieldsData = useCallback((config) => {
    const { projectList } = config;

    const applicationEnvironmentNameList = [];
    const applicationId = _.map(projectList, ({ application, environment }) => {
      const applicationName = applicationRef.current[application];
      const environmentName = _.isArray(environment)? _.map(environment, v => environmentRef.current[v]).join(ENV_DELIMITER) : environmentRef.current[environment];
      if(!_.isEmpty(environment)){
        applicationEnvironmentNameList.push(applicationName + DELIMITER + environmentName);
        return `${application}${DELIMITER}${_.isArray(environment) ? environment.join(ENV_DELIMITER): environment}`;
      }
      applicationEnvironmentNameList.push(applicationName);
      return application;
    });

    
    return ({
      appId: applicationId.join(),
      appName: applicationEnvironmentNameList.join(),
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, [ ]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { appId } = config;
    const applicationsWithEnvironments = appId?.split(',');
    const projects = _.map(applicationsWithEnvironments, applicationWithEnvironment => {
      const index = applicationWithEnvironment.lastIndexOf(DELIMITER);
      if(index !== -1){
        const application = applicationWithEnvironment.substring(0, index);
        const environment = applicationWithEnvironment.substring(index + 1, applicationWithEnvironment.length);
        return { application, environment: environment.split(ENV_DELIMITER) };
      }
      return { project: applicationWithEnvironment }
    });
    return ({
      projectList: projects
        });
  }, []);
 
  const onValuesChange = useCallback((values, ...args) => {
    
    if(values.configurations){
      let isApplicationChanged = false;
        const formValues = form.getFieldsValue();
        _.forEach(values.configurations, (configs, index) => {


          if(!configs || !configs.projectList || configs.projectList.length > 1){
            return;
          }

          if(!configs || _.isString(configs.projectList)){
            return;
          }
          _.forEach(configs.projectList, (object, i) => {
            if(object && object.application && Object.keys(object).length === 1){
              isApplicationChanged = true;
              formValues.configurations[index].projectList[i].environment = undefined;
            }
          })
        });
        if(isApplicationChanged){
          form.setFieldsValue(formValues);
        }
    }
  }, [form]);

  const projectListRender = useCallback((listField) => {

    let environmentAPI = environmentsApiRef.current[listField.name];

    if(!environmentAPI){

      environmentAPI = environmentsApiRef.current[listField.name] = ({ listFieldName: nestedListFieldName, ...params }) => {
        const allConfigs = form.getFieldsValue().configurations;
        const application = allConfigs[listField.name]?.projectList[nestedListFieldName]?.application;

        if (!application) {
          return { promise: Promise.resolve([]), cancel: _.noop };
        }
        return fetchEnvironment({ ...params, project: application });
      }

    }

    const __fields = [{
      name: 'application',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: disableProjectFields,
      placeholder: "Applications",
      colSpan: 9,
      props: {
        showValueOnload: false,
        autoFocus: !disableProjectFields,
        optionsMutator: (projects) => {
          const selected = _.compact(_.map(form.getFieldsValue().configurations?.[listField.name]?.projectList, p => p?.application));
          return _.map(projects, p => ({ ...p, disabled: _.includes(selected, p.id) }));
        },
        api: fetchApplications
      },
      rules: [{ required: true, message: 'Application is required' }]
    }, {
      name: 'environment',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: disableProjectFields,
      colSpan: 12,
      props: {
        placeholder: "Environments",
        api: environmentAPI,
        mode: "multiple",
        setExtraProps: (field, nestedListField) => {
          const allConfigs = form.getFieldsValue().configurations;
          const application = allConfigs[listField.name]?.projectList[nestedListField.name]?.application;
          return {
            showValueOnload: false,
            resetOptionsProp: application
          }
        },
        onChange: () => {
          setEnvironmentChanged(!_.isEmpty(props.record))
        }
      },
    }, {
      name: "add",
      colSpan: 2,
      render: ({ remove, name, fields }) => {
        return <Button disabled={fields.length === 1} type='dashed' onClick={() => { remove(name) }}>-</Button>;
      }
    }]

    return (
      <div className='m-b-sm'>
        <Form.List name={[listField.name, "projectList"]}>
          {(fields, { add, remove }) => {
            return (
              <div>
                {_.map(fields, field => {
                  return generateFormFields(__fields, 9, { ...field/* , name: [listField.name, field.name] */, add, remove, fields }, field.name);
                })}
                <Button size='small' type='dashed' onClick={() => add({ project: undefined })} disabled={disableProjectFields}>+ Add Project</Button>
                {environmentChanged && <Alert className="m-t-m" message="Adding/removing application or environment from an existing mapping might resolve findings from the previous mapped environment." type="warning" />}
              </div>
            )
          }}
        </Form.List>
      </div>
    )
  }, [disableProjectFields, form, fetchApplications, fetchEnvironment, environmentChanged, props.record]);


  const _fields = useMemo(() => [{
    name: "label",
    type: FieldTypes.LABEL,
    value: <b className='required-mark'>Application</b>,
    style: {marginBottom: -24},
    colSpan: 9
  }, {
    name: "label",
    type: FieldTypes.LABEL,
    value: <b className='required-mark'>Environment</b>,
    style: {marginBottom: -24},
    colSpan: 9
  }, {
    name: (listField) => [listField.name, "projectList"],
    render: projectListRender
  },
   {
    type: FieldTypes.DIVIDER
  }], [projectListRender]);

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={_fields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      {...props}
      onValuesChange={onValuesChange}
    />
  )
})

export default StackhawkMappingForm;